import React, { useEffect, useState } from "react";
import { Checkbox, Dialog, FormControlLabel, TextField } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ForwardToInboxRoundedIcon from "@mui/icons-material/ForwardToInboxRounded";
import PictureAsPdfRoundedIcon from "@mui/icons-material/PictureAsPdfRounded";
import { Spinner } from "../../components/UI/States";
import { Notice } from "../../components/Fleet/FleetParts";
import { downloadText } from "../../components/Fleet/fleetFormat";
import { emailSafetyReport, fetchSafetyReportPdf, previewSafetyReport } from "./safetyApi";
import { DialogHeader, PeoplePicker, PersonOption, plural } from "./SafetyParts";

// "Download PDF" / "Email PDF" for the Safety pages. The server builds the
// report from `selection` — the ids of the rows on screen, in order — so the
// PDF always matches the filters the person set. Emailing suggests the
// people the work belongs to (the last inspector, the assignees who aren't
// done, …); anyone in the directory can be added. Server:
// server/routes/api/Safety/safetyReports.js.

/** One line describing the filters, printed under the report's title. */
export const reportScope = ({ branch, status, query }) =>
  [branch || "All branches", status, query && `matching “${query.trim()}”`].filter(Boolean).join(" · ");

const MAX_RECIPIENTS = 50;

const EmailReportDialog = ({ open, kind, selection, userId, tone, onClose }) => {
  const [shown, setShown] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loadError, setLoadError] = useState(null);
  const [to, setTo] = useState([]);
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [copyMe, setCopyMe] = useState(true);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [sent, setSent] = useState(null);

  // The selection is read once per opening — the dialog emails what the page
  // showed when it was opened.
  useEffect(() => {
    if (!open) return undefined;
    setShown(selection);
    setPreview(null);
    setLoadError(null);
    setTo([]);
    setSubject("");
    setMessage("");
    setCopyMe(true);
    setSending(false);
    setError(null);
    setSent(null);
    let active = true;
    previewSafetyReport(userId, kind, selection)
      .then((p) => {
        if (!active) return;
        setPreview(p);
        setSubject(p.subject);
      })
      .catch((e) => active && setLoadError(e.message));
    return () => {
      active = false;
    };
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = (p) => setTo((list) => (list.some((x) => x.email === p.email) ? list : [...list, p]));
  const remove = (email) => setTo((list) => list.filter((p) => p.email !== email));
  const suggested = preview?.people || [];
  const notAdded = suggested.filter((p) => !to.some((x) => x.email === p.email));
  const canSend = preview && to.length > 0 && to.length <= MAX_RECIPIENTS && !sending;

  const send = async () => {
    setSending(true);
    setError(null);
    try {
      const res = await emailSafetyReport(userId, kind, shown, {
        to: to.map((p) => p.email),
        subject,
        message,
        copyMe,
      });
      setSent(res);
    } catch (err) {
      setError(err.message);
    } finally {
      setSending(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={sending ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{ sx: { m: 2 } }}
      aria-labelledby="email-report-title"
    >
      <div className="p-5 sm:p-6">
        <DialogHeader
          id="email-report-title"
          icon={ForwardToInboxRoundedIcon}
          tone={tone}
          title="Email this report"
          subtitle={preview?.title}
          onClose={onClose}
          closeDisabled={sending}
        />

        {loadError && (
          <div className="mt-4">
            <Notice tone="rose">{loadError}</Notice>
          </div>
        )}
        {!preview && !loadError && (
          <div className="mt-4 flex items-center gap-2 text-sm">
            <Spinner size={18} /> <span className="app-muted">Getting the report ready…</span>
          </div>
        )}

        {preview && sent && (
          <div className="mt-5">
            <p className="flex items-start gap-2 text-sm font-semibold">
              <CheckCircleRoundedIcon className="text-emerald-600 dark:text-emerald-400" fontSize="small" />
              Sent to {plural(sent.sent.length, "person", "people")}.
            </p>
            <p className="app-muted mt-2 text-sm">
              {to.map((p) => p.name).join(", ")}
              {sent.cc ? " — and a copy to you." : "."} Replies come to your email.
            </p>
            <div className="mt-5 flex justify-end">
              <button type="button" className="app-btn app-btn-primary" onClick={onClose}>
                Done
              </button>
            </div>
          </div>
        )}

        {preview && !sent && (
          <div className="mt-5 space-y-5">
            <div className="app-surface flex items-center gap-3 rounded-xl px-3 py-2 text-sm">
              <PictureAsPdfRoundedIcon className="shrink-0 text-rose-600 dark:text-rose-400" fontSize="small" />
              <span className="min-w-0">
                <span className="block truncate font-semibold">{preview.filename}</span>
                <span className="app-muted block text-xs">
                  {[preview.count, preview.scope].filter(Boolean).join(" · ")}
                </span>
              </span>
            </div>

            <section>
              <p className="app-eyebrow mb-2">To</p>
              <PeoplePicker
                userId={userId}
                people={to}
                onAdd={add}
                onRemove={remove}
                placeholder="Add someone by name, email, or branch…"
              />
              {to.length > MAX_RECIPIENTS && (
                <p className="mt-2 text-xs font-semibold text-rose-700 dark:text-rose-300">
                  A report can go to at most {MAX_RECIPIENTS} people at once.
                </p>
              )}
            </section>

            {suggested.length > 0 && (
              <section>
                <div className="mb-2 flex items-center justify-between gap-3">
                  <p className="app-eyebrow">Suggested — the people this work is on</p>
                  {notAdded.length > 1 && (
                    <button
                      type="button"
                      className="app-link text-xs font-semibold"
                      onClick={() => notAdded.forEach(add)}
                    >
                      Add all {notAdded.length}
                    </button>
                  )}
                </div>
                <ul className="max-h-56 divide-y divide-[color:var(--app-divider)] overflow-y-auto rounded-xl border app-divider">
                  {suggested.map((p) => (
                    <li key={p.email}>
                      <PersonOption
                        person={p}
                        detail={[p.branch, p.reason].filter(Boolean).join(" · ")}
                        added={to.some((x) => x.email === p.email)}
                        onAdd={add}
                      />
                    </li>
                  ))}
                </ul>
              </section>
            )}

            <section className="space-y-3">
              <TextField
                size="small"
                fullWidth
                label="Subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                inputProps={{ maxLength: 150 }}
              />
              <TextField
                size="small"
                fullWidth
                multiline
                minRows={3}
                label="Message (optional)"
                placeholder="What should they do with it?"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                inputProps={{ maxLength: 2000 }}
              />
              <FormControlLabel
                control={<Checkbox size="small" checked={copyMe} onChange={(e) => setCopyMe(e.target.checked)} />}
                label={<span className="text-sm">Send me a copy</span>}
              />
            </section>

            {error && <Notice tone="rose">{error}</Notice>}

            <div className="flex flex-wrap items-center justify-between gap-3 border-t app-divider pt-4">
              <span className="app-muted text-xs">Sent from Big C Node with your name on it. Replies come to you.</span>
              <span className="flex gap-2">
                <button type="button" className="app-btn app-btn-ghost" onClick={onClose} disabled={sending}>
                  Cancel
                </button>
                <button type="button" className="app-btn app-btn-primary" onClick={send} disabled={!canSend}>
                  {sending ? "Sending…" : to.length ? `Send to ${plural(to.length, "person", "people")}` : "Send"}
                </button>
              </span>
            </div>
          </div>
        )}
      </div>
    </Dialog>
  );
};

/**
 * The two report buttons for a page or dialog. `selection` is what's on
 * screen (see safetyApi.js "Reports"); `disabled` when there's nothing to
 * report.
 */
export const ReportActions = ({ kind, selection, userId, tone = "slate", disabled, className = "" }) => {
  const [downloading, setDownloading] = useState(false);
  const [emailing, setEmailing] = useState(false);
  const [error, setError] = useState(null);

  const download = async () => {
    setDownloading(true);
    setError(null);
    try {
      const { blob, filename } = await fetchSafetyReportPdf(userId, kind, selection);
      downloadText(filename, blob, "application/pdf");
    } catch (err) {
      setError(err.message);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className={`flex flex-col gap-1 ${className}`}>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          className="app-btn app-btn-secondary"
          onClick={download}
          disabled={disabled || downloading}
        >
          <PictureAsPdfRoundedIcon sx={{ fontSize: 18 }} /> {downloading ? "Creating PDF…" : "Download PDF"}
        </button>
        <button type="button" className="app-btn app-btn-secondary" onClick={() => setEmailing(true)} disabled={disabled}>
          <ForwardToInboxRoundedIcon sx={{ fontSize: 18 }} /> Email PDF
        </button>
      </div>
      {error && (
        <p role="alert" className="text-xs font-semibold text-rose-700 dark:text-rose-300">
          {error}
        </p>
      )}
      <EmailReportDialog
        open={emailing}
        kind={kind}
        selection={selection}
        userId={userId}
        tone={tone}
        onClose={() => setEmailing(false)}
      />
    </div>
  );
};
